import type { MouseEvent } from "react";
import { novelLinkKey } from "@lorevok/domain";
import { authorRouteKey } from "./authorRoute.js";
import { buildPath } from "./route.js";

// Real links for in-app navigation. Cards, bylines and nav items are <a href>
// elements — shareable, crawlable, openable in a new tab — that still route
// in-app on a plain click (the hand-rolled router in App.tsx owns navigation).

/**
 * True for a plain left click: the one click the app handles itself. Any
 * modifier (new tab / window, download) or another button is left to the
 * browser, which then follows the href.
 */
export function isPlainLeftClick(event: MouseEvent<HTMLElement>): boolean {
  return (
    event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && !event.defaultPrevented
  );
}

/** A link's onClick: navigate in-app on a plain left click, otherwise let the browser follow the href. */
export function followInApp(event: MouseEvent<HTMLAnchorElement>, navigate: () => void): void {
  if (!isPlainLeftClick(event)) return;
  event.preventDefault();
  navigate();
}

/**
 * The URL segment a novel's links use. A public novel is linked by its short
 * link key (`<slug>-<code>` — its canonical address). Any other novel (a draft
 * in My Novels, an unlisted one) by its id: a slug is made from the title, and
 * a private title must not travel in hrefs (analytics records link hrefs).
 */
export function novelLinkTarget(novel: { id: string; title: string; visibility?: string; short_code?: string }): string {
  return novel.visibility === "public" && novel.short_code ? novelLinkKey(novel.title, novel.short_code) : novel.id;
}

/** `/novel/<key>` (the detail page) or `/play/<key>` (the player) for a novel. */
export function novelHref(
  page: "detail" | "player",
  novel: { id: string; title: string; visibility?: string; short_code?: string }
): string {
  return buildPath(page, novelLinkTarget(novel));
}

/** `/u/<username>` — or `/u/<owner id>` for an owner without a profile. */
export function authorHref(ownerId: string, username?: string | null): string {
  return buildPath("author", authorRouteKey(ownerId, username));
}
